import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { userSettingsApi } from '../../utils/api';

/** Style commun des champs : une seule définition pour garder les saisies homogènes */
const INPUT_CLASS =
  'block w-full rounded-md border-gray-300 text-sm text-gray-900 shadow-sm transition duration-150 placeholder:text-gray-400 focus:border-primary-500 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500';

/**
 * Message de retour du formulaire (succès ou erreur) : icône + texte, avec le
 * rôle ARIA adapté pour être annoncé par les lecteurs d'écran.
 */
const FormMessage = ({ type, children }) => {
  const isError = type === 'error';
  const Icon = isError ? ExclamationCircleIcon : CheckCircleIcon;
  return (
    <div
      role={isError ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-md border p-3 text-sm ${
        isError ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'
      }`}
    >
      <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
};

/** Squelette reprenant la forme du formulaire pendant le chargement */
const FormSkeleton = () => (
  <div className="animate-pulse space-y-6" aria-hidden="true">
    <div className="grid gap-5 sm:grid-cols-2">
      <div className="space-y-2"><div className="h-3 w-24 rounded bg-gray-200" /><div className="h-9 rounded-md bg-gray-100" /></div>
      <div className="space-y-2"><div className="h-3 w-16 rounded bg-gray-200" /><div className="h-9 rounded-md bg-gray-100" /></div>
    </div>
    <div className="h-px bg-gray-200" />
    <div className="grid gap-5 sm:grid-cols-2">
      <div className="space-y-2"><div className="h-3 w-32 rounded bg-gray-200" /><div className="h-9 rounded-md bg-gray-100" /></div>
      <div className="space-y-2"><div className="h-3 w-36 rounded bg-gray-200" /><div className="h-9 rounded-md bg-gray-100" /></div>
    </div>
  </div>
);

const ProfileForm = () => {
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    new_password: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      // L'API initialise l'essai gratuit côté serveur si les réglages n'existent pas encore.
      const response = await userSettingsApi.getCurrent();
      const userSettings = response.data?.data || {};

      setProfile({
        id: user.id,
        full_name: userSettings.full_name || user.user_metadata?.full_name || '',
        email: user.email,
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validatePasswords = () => {
    if (profile.new_password && profile.new_password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return false;
    }
    if (profile.new_password !== profile.confirm_password) {
      setError('Les mots de passe ne correspondent pas');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (profile.new_password && !validatePasswords()) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Le nom passe par l'API afin de ne jamais donner d'accès direct aux champs sensibles.
      await userSettingsApi.update({ full_name: profile.full_name });

      // Update password if provided
      if (profile.new_password) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: profile.new_password,
        });

        if (passwordError) throw passwordError;
      }

      setSuccess('Profil mis à jour avec succès');
      setProfile((prev) => ({
        ...prev,
        new_password: '',
        confirm_password: '',
      }));
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Erreur lors de la mise à jour du profil');
    } finally {
      setSaving(false);
    }
  };

  // Squelette à la forme du formulaire plutôt qu'un spinner générique
  if (loading) return <FormSkeleton />;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && <FormMessage type="error">{error}</FormMessage>}
      {success && <FormMessage type="success">{success}</FormMessage>}

      {/* Identité : nom et e-mail côte à côte dès le format tablette */}
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
            Nom complet
          </label>
          <input
            type="text"
            name="full_name"
            id="full_name"
            autoComplete="name"
            value={profile.full_name || ''}
            onChange={handleChange}
            className={`mt-1.5 ${INPUT_CLASS}`}
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Adresse e-mail
          </label>
          <input
            type="email"
            name="email"
            id="email"
            value={profile.email || ''}
            disabled
            aria-describedby="email-aide"
            className={`mt-1.5 ${INPUT_CLASS}`}
          />
          {/* Explique pourquoi le champ est grisé */}
          <p id="email-aide" className="mt-1.5 text-xs text-gray-500">
            L’adresse de connexion ne peut pas être modifiée ici.
          </p>
        </div>
      </div>

      {/* Mot de passe : groupe de champs distinct, séparé par un filet */}
      <fieldset className="border-t border-gray-200 pt-6">
        <legend className="sr-only">Changer le mot de passe</legend>
        <h3 className="text-sm font-semibold text-gray-900">Changer le mot de passe</h3>
        <p className="mt-1 text-sm text-gray-500">Laissez ces champs vides pour conserver le mot de passe actuel.</p>

        <div className="mt-4 grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="new_password" className="block text-sm font-medium text-gray-700">
              Nouveau mot de passe
            </label>
            <input
              type="password"
              name="new_password"
              id="new_password"
              autoComplete="new-password"
              value={profile.new_password}
              onChange={handleChange}
              className={`mt-1.5 ${INPUT_CLASS}`}
            />
          </div>

          <div>
            <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700">
              Confirmer le mot de passe
            </label>
            <input
              type="password"
              name="confirm_password"
              id="confirm_password"
              autoComplete="new-password"
              value={profile.confirm_password}
              onChange={handleChange}
              className={`mt-1.5 ${INPUT_CLASS}`}
            />
          </div>
        </div>
      </fieldset>

      {/* Pied de formulaire : rappel de la règle de validation et action principale */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-5">
        <p className="text-xs text-gray-500">Le mot de passe doit contenir au moins 6 caractères.</p>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition duration-150 hover:bg-primary-700 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-primary-400"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
        </button>
      </div>
    </form>
  );
};

export default ProfileForm;
